import type { FC, ReactElement, ReactNode } from 'react';
import { createContext, useContext } from 'react';

import type { GetRooftopFeaturesResponseDTO } from 'declarations/dto/features';
import { FeaturesAppsE, type FeaturesNamesE } from 'declarations/enums/features';
import { useFetchRooftopFeatures } from 'hooks/api/useRooftopFeatures';

import { useTickets } from './TicketsContext';

export type FeaturesContextT = {
  rootTopFeatures: GetRooftopFeaturesResponseDTO | undefined;
  isFeatureActive: (featureName: FeaturesNamesE) => boolean;
};

type FeaturesProviderPropsT = { children: ReactNode };

const contextDefaultValue = {
  rootTopFeatures: undefined,
  isFeatureActive: (): boolean => false,
};

export const FeaturesContext = createContext<FeaturesContextT>(contextDefaultValue);

const FeaturesProvider: FC<FeaturesProviderPropsT> = ({ children }): ReactElement => {
  /**
   * Hooks
   */
  const { selectedRoofTop } = useTickets();

  /**
   * Api hooks
   */
  const getFeaturesResult = useFetchRooftopFeatures(selectedRoofTop);

  /**
   * Handlers
   */
  const isFeatureActive = (featureName: FeaturesNamesE): boolean => {
    return (
      !!selectedRoofTop &&
      !!getFeaturesResult.data?.some(
        ({ applicationName, featureName: name, recordAudit }) =>
          applicationName.toLowerCase() === FeaturesAppsE.Chirp &&
          name.toLowerCase() === featureName.toLowerCase() &&
          recordAudit.isActive
      )
    );
  };

  return (
    <FeaturesContext.Provider
      value={{
        rootTopFeatures: getFeaturesResult.data,
        isFeatureActive,
      }}
    >
      {children}
    </FeaturesContext.Provider>
  );
};

const useFeatures = (): FeaturesContextT => useContext(FeaturesContext);

export { FeaturesProvider, useFeatures };
